/**
 * The intake batch state machine (spec 8.1, IP-60): SPEC's set, minus the
 * upload and quarantine-release machinery IP-44 makes unnecessary.
 *
 *   RECEIVED -> SCANNING -> VALIDATED | QUARANTINED (any ERROR finding)
 *   SCANNING -> REJECTED_MALWARE | SCAN_FAILED
 *   VALIDATED -> ACCEPTED -> PROCESSED
 *   VALIDATED -> REJECTED (a checker, with a reason)
 *   VALIDATED -> SUPERSEDED (a later live file for the same fingerprint)
 *   VALIDATED -> ACCEPTANCE_EXPIRED (48 h after validated_at, applied lazily
 *     when the batch is read or accepted - there is no sweeper, D-6)
 *
 * IP-91..93: RECEIVED now exists only inside the upload ticket's own
 * transaction - the batch is created and moved to SCANNING (its object key
 * fixed, its version still NULL) before the presigned POST is ever returned.
 * SCANNING -> SCAN_FAILED also happens with no verdict at all: a batch still
 * SCANNING SCAN_VERDICT_TIMEOUT_MS after created_at lapses (scan_status
 * NO_VERDICT), applied lazily wherever the 48 h expiry is (IP-93, D-6) - an
 * upload whose form was never posted, or whose verdict never landed, frees
 * its fingerprint instead of holding it forever. The FAIL event already
 * names that transition.
 *
 * This module is pure: no q, no clock port beyond the milliseconds it is
 * handed. sql.js's mark*() functions do the actual conditional UPDATEs; this
 * is what they (and upload.js/scan.js/accept/reject) reason about before and
 * instead of hitting the database.
 *
 * Fix round 1: acceptBatch and rejectBatch cannot themselves make
 * ACCEPTANCE_EXPIRED durable - both run inside the caller's withTx, which
 * rolls the whole transaction back on any throw, undoing a markExpired write
 * along with the refusal (see accept-reject.js's own comment). reads.js's
 * expireStale(q, nowIso) is the durable path: one UPDATE for every stale
 * VALIDATED batch (and, IP-93, one for every lapsed SCANNING one), called by
 * the upload checks (before the duplicate-fingerprint and daily-cap checks,
 * so a stale batch's fingerprint is free again) and by every read that lists
 * or shows a batch.
 *
 * Fix round 2: constraintOf/sqlStateOf and DEAD_STATES exist so upload.js's
 * duplicate-file mapping reads a database refusal the same way whether it
 * ran on PGlite/node-postgres or the real Data API, and so its query and the
 * live-fingerprint index's own predicate share one list instead of two.
 *
 * IP-103 (migration 0012): six classes, the four Core data ones each INITIAL
 * or DAILY (load_kind, mapped as loadKind; NULL for a NAV file or a
 * statement). The key layout is unchanged - intake/console/<CLASS>/<batchId>/
 * <sha256>.csv - so INTAKE_OBJECT_KEY admits the six classes and nothing
 * else. A DAILY batch is accepted on its own Colombo day or not at all
 * (dailyExpired): the lazy expiry ends one still VALIDATED after midnight
 * (sql.js expireStale), and the accept refuses one whose day has passed
 * (DAILY_NOT_TODAY).
 */
import { DomainError } from '../errors.js';
import { colomboBusinessDate } from '../orders/dealing-date.js';
import { FILE_CLASSES } from './kinds.js';

export const BATCH_STATE = Object.freeze({
  RECEIVED: 'RECEIVED',
  SCANNING: 'SCANNING',
  VALIDATED: 'VALIDATED',
  QUARANTINED: 'QUARANTINED',
  REJECTED_MALWARE: 'REJECTED_MALWARE',
  SCAN_FAILED: 'SCAN_FAILED',
  ACCEPTED: 'ACCEPTED',
  PROCESSED: 'PROCESSED',
  REJECTED: 'REJECTED',
  SUPERSEDED: 'SUPERSEDED',
  ACCEPTANCE_EXPIRED: 'ACCEPTANCE_EXPIRED',
});

/** Acceptance expires 48 h after validated_at (global-constraints.md). */
export const ACCEPTANCE_EXPIRY_MS = 48 * 60 * 60 * 1000;

/**
 * IP-93: a batch still SCANNING this long after created_at has had no
 * verdict, and lapses to SCAN_FAILED (NO_VERDICT). sql.js's lapseUnscanned
 * writes the same 30 minutes as an interval literal; batch.test.js holds the
 * two together.
 */
export const SCAN_VERDICT_TIMEOUT_MS = 30 * 60 * 1000;

/** The key an upload is stored under - its class, its batch and the SHA-256 its uploader's code was bound to. */
export function objectKeyOf({ fileClass, batchId, sha256 }) {
  return 'intake/console/' + fileClass + '/' + batchId + '/' + sha256 + '.csv';
}

/** Exactly the keys objectKeyOf makes, for the six classes: what the verdict consumer accepts as an upload (IP-95, IP-103). */
export const INTAKE_OBJECT_KEY = new RegExp('^intake/console/(' + FILE_CLASSES.join('|') + ')/[0-9a-f-]{36}/[0-9a-f]{64}\\.csv$');

/** GuardDuty's scanResultStatus for a file it found nothing in - what scan.js records as scan_status on a clean scan. */
export const SCAN_CLEAR = 'NO_THREATS_FOUND';

/**
 * The states whose file the Console may download again (GET
 * intake/batches/{id}/file): every state a clean scan of the bound version
 * leads to - VALIDATED, QUARANTINED (its rows have errors, but the bytes were
 * scanned clean and re-hashed), and everything after VALIDATED. Never
 * RECEIVED or SCANNING (not scanned yet), REJECTED_MALWARE (a threat) or
 * SCAN_FAILED (not scanned, no verdict, or not the bytes the code was bound
 * to - FINGERPRINT_MISMATCH).
 */
export const DOWNLOADABLE_STATES = Object.freeze([
  BATCH_STATE.VALIDATED, BATCH_STATE.QUARANTINED, BATCH_STATE.ACCEPTED, BATCH_STATE.PROCESSED,
  BATCH_STATE.REJECTED, BATCH_STATE.SUPERSEDED, BATCH_STATE.ACCEPTANCE_EXPIRED,
]);

/**
 * Whether a batch's file may be handed out (reads.js batchFile; the Console
 * greys the action out by the same test). All four must hold:
 *   - its state is one of DOWNLOADABLE_STATES;
 *   - its scan status is SCAN_CLEAR - the scan cleared this very batch;
 *   - a version is bound (IP-92) - the one scanned and re-hashed, and the only
 *     one the download reads;
 *   - its key is exactly objectKeyOf(batch) - intake/console/<CLASS>/<batchId>/
 *     <sha256>.csv - so the read can never reach another object, whatever a
 *     row says.
 *
 * @param {object} batch mapBatch's shape
 */
export function isDownloadable(batch) {
  return Boolean(batch)
    && DOWNLOADABLE_STATES.includes(batch.state)
    && batch.scanStatus === SCAN_CLEAR
    && typeof batch.objectVersion === 'string' && batch.objectVersion !== ''
    && batch.objectKey === objectKeyOf(batch);
}

/** The longest name a download is saved under, before its .csv. */
const DOWNLOAD_STEM_CHARS = 120;

/**
 * The name a downloaded file is saved under (C-32, spec 37.7.5): never the
 * stored name verbatim, which is the uploader's - and before the server
 * required .csv, could have been statement.iqy or report.csv.hta. Every
 * character outside [A-Za-z0-9._ -] becomes _, runs of _ collapse, leading
 * dots and spaces and trailing dots and spaces go, the stem is cut to 120
 * characters, and exactly one .csv ends it; an empty stem is "file". The
 * server answers with it (reads.js batchFile) and the Console saves under it
 * again, whatever the server said.
 *
 * @param {string} storedName intake_batch.file_name
 * @returns {string} a name ending .csv
 */
export function downloadName(storedName) {
  const stem = String(storedName ?? '').replace(/\.csv$/i, '')
    .replace(/[^A-Za-z0-9._ -]/g, '_')
    .replace(/_+/g, '_')
    .replace(/^[.\s]+/, '')
    .slice(0, DOWNLOAD_STEM_CHARS)
    .replace(/[.\s]+$/, '');
  return (stem || 'file') + '.csv';
}

/**
 * States the live-fingerprint unique indexes cover (migration 0006
 * intake_batch_live_fingerprint, verbatim; 0012 re-creates it for every batch
 * but a DAILY one and adds intake_batch_live_fingerprint_daily, per business
 * date, with the same states).
 */
const LIVE_STATES = new Set([
  BATCH_STATE.RECEIVED, BATCH_STATE.SCANNING, BATCH_STATE.VALIDATED,
  BATCH_STATE.QUARANTINED, BATCH_STATE.ACCEPTED, BATCH_STATE.PROCESSED,
]);

/**
 * The complement of LIVE_STATES - what the index's own WHERE clause spells
 * as `state not in (...)`. sql.js's selectLiveBatchByFingerprint builds its
 * NOT IN list from this (via sql/statement.js's constants()), so the index's
 * partial-index predicate and the query that reasons about it never drift
 * apart into two hand-written lists.
 */
export const DEAD_STATES = Object.freeze(Object.values(BATCH_STATE).filter((state) => !LIVE_STATES.has(state)));

const TRANSITIONS = Object.freeze({
  RECEIVED: Object.freeze({ SCAN: BATCH_STATE.SCANNING }),
  SCANNING: Object.freeze({
    CLEAR: BATCH_STATE.VALIDATED,
    QUARANTINE: BATCH_STATE.QUARANTINED,
    THREAT: BATCH_STATE.REJECTED_MALWARE,
    FAIL: BATCH_STATE.SCAN_FAILED,
  }),
  VALIDATED: Object.freeze({
    ACCEPT: BATCH_STATE.ACCEPTED,
    REJECT: BATCH_STATE.REJECTED,
    SUPERSEDE: BATCH_STATE.SUPERSEDED,
    EXPIRE: BATCH_STATE.ACCEPTANCE_EXPIRED,
  }),
  ACCEPTED: Object.freeze({ PROCESS: BATCH_STATE.PROCESSED }),
});

/** The state after `event` fires in `from`, or DomainError('INTAKE_INVALID_TRANSITION', 409). */
export function nextState(from, event) {
  const to = TRANSITIONS[from]?.[event];
  if (!to) {
    throw new DomainError('INTAKE_INVALID_TRANSITION', 'an intake batch cannot ' + event + ' from ' + from, {
      status: 409,
      details: { from, event },
    });
  }
  return to;
}

/** True for a state the live-fingerprint index still holds (a re-upload of the same bytes is refused). */
export function isLive(state) {
  return LIVE_STATES.has(state);
}

/**
 * True once 48 h have passed since validated_at (IP-60; applied lazily, no
 * sweeper - D-6). A batch never validated cannot be acceptance-expired.
 *
 * @param {{ validatedAt: string|null }} batch validatedAt an ISO instant, or null
 * @param {number} nowMs
 */
export function acceptanceExpired(batch, nowMs) {
  if (!batch?.validatedAt) return false;
  return nowMs > Date.parse(batch.validatedAt) + ACCEPTANCE_EXPIRY_MS;
}

/**
 * IP-103: true once a DAILY batch's business date is before today in Colombo
 * - a daily file is accepted on its own day or not at all. Applied lazily,
 * with the 48 h expiry (sql.js expireStale). Never true of an INITIAL batch, a
 * NAV file or a statement.
 *
 * @param {{ loadKind: string|null, businessDate: string }} batch mapBatch's shape
 * @param {number} nowMs
 */
export function dailyExpired(batch, nowMs) {
  return batch?.loadKind === 'DAILY' && batch.businessDate < colomboBusinessDate(nowMs);
}

/**
 * The CHECK/unique constraint name behind a database refusal, from PGlite /
 * node-postgres (error.constraint) or the Data API (a DatabaseErrorException
 * whose message carries 'constraint "name"') - the one place upload.js and
 * accept-reject.js share this so each maps only the constraints it owns to
 * its own DomainError (INTAKE_DUPLICATE_FILE, MAKER_IS_CHECKER).
 */
export function constraintOf(error) {
  if (typeof error?.constraint === 'string') return error.constraint;
  return /constraint "([a-z0-9_]+)"/.exec(String(error?.message ?? ''))?.[1] ?? null;
}

/**
 * The SQLSTATE behind a database refusal - PGlite/node-postgres gives it as
 * error.code; the Data API gives no .code at all (a DatabaseErrorException),
 * only 'SQLState: nnnnn' inside .message (the same convention
 * authority/access.js's domainErrorFromDb reads). Fix round 2: upload.js's
 * first pass gated on `error.code !== '23505'` alone, which is true for
 * every Data API error (no .code), so the Data API leaked every raw error
 * this was meant to catch. Checking sqlState AND constraintOf(error) together
 * is what tells a genuine live-fingerprint collision apart from some other
 * 23505 (a different constraint) whose message happens to mention this
 * one's name.
 */
export function sqlStateOf(error) {
  if (typeof error?.code === 'string' && /^[0-9A-Z]{5}$/.test(error.code)) return error.code;
  return /SQLState:\s*([0-9A-Z]{5})/i.exec(String(error?.message ?? ''))?.[1] ?? null;
}

/**
 * A raw intake_batch row (as q returns it: snake_case, jsonb as text) to the
 * shape every intake function returns - camelCase, summary parsed. The one
 * place this module and sql.js agree on the batch's shape.
 */
export function mapBatch(row) {
  if (!row) return null;
  return {
    batchId: row.batch_id,
    fileClass: row.file_class,
    loadKind: row.load_kind ?? null,
    businessDate: row.business_date,
    accountId: row.account_id,
    fileName: row.file_name,
    sha256: row.sha256,
    contentLength: row.content_length,
    objectKey: row.object_key,
    objectVersion: row.object_version,
    state: row.state,
    uploadedBy: row.uploaded_by,
    uploadEvidenceId: row.upload_evidence_id,
    scanStatus: row.scan_status,
    rowCount: row.row_count,
    warningCount: row.warning_count,
    errorCount: row.error_count,
    summary: typeof row.summary === 'string' ? JSON.parse(row.summary) : (row.summary ?? null),
    summarySha256: row.summary_sha256,
    validatedAt: row.validated_at,
    acceptedBy: row.accepted_by,
    acceptEvidenceId: row.accept_evidence_id,
    acceptedAt: row.accepted_at,
    rejectedBy: row.rejected_by,
    rejectReason: row.reject_reason,
    supersededBy: row.superseded_by,
    processedAt: row.processed_at,
    createdAt: row.created_at,
  };
}
